import { Button } from '@/components/ui-kit/button';
import { useTranslation } from 'react-i18next';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Trash2 } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { AgentBehaviorSettingsForm } from '@/modules/gpt-chats/components/agents-configuration/components/config-card-section/utils';
import { showErrorToast } from '@/hooks/use-toast';
import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Switch } from '@/components/ui-kit/switch';
import { InfoTooltip } from '@/components/ui-kit/info-tool-tip';
import { SEVERITY_SCORES, getSeverityColor } from './utils';
import useIsMobile from '@/hooks/use-is-mobile';

type CustomRulesListProps = {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  onSave: () => Promise<void>;
  isMobile?: boolean;
};

// const getSeverityLabel = (severity: string): string => {
//   const label = severity.charAt(0).toUpperCase() + severity.slice(1);

//   if (severity === 'block') {
//     return label;
//   }

//   const score = SEVERITY_SCORES[severity] ?? 0;
//   return `${label} (${score.toFixed(2)})`;
// };

export const CustomRulesList = ({ form, onSave }: CustomRulesListProps) => {
  const { t } = useTranslation();
  const customRules = form.watch('guardrail.custom_rules') || [];
  const isCustomRulesEnabled = form.watch('guardrail.enable_custom_rules');
  const [validationErrors, setValidationErrors] = useState<Record<number, string[]>>({});

  const isXlBreakpoint = useIsMobile();

  const validateRule = (index: number): boolean => {
    const rule = customRules[index];
    const ruleErrors: string[] = [];
    let isValid = true;

    if (!rule.name || rule.name.trim() === '') {
      ruleErrors.push(t('RULE_NAME_REQUIRED'));
      isValid = false;
    }

    if (!rule.pattern || rule.pattern.trim() === '') {
      ruleErrors.push(t('PATTERN_REQUIRED'));
      isValid = false;
    }

    if (!rule.message || rule.message.trim() === '') {
      ruleErrors.push(t('MESSAGE_REQUIRED'));
      isValid = false;
    }

    if (ruleErrors.length > 0) {
      setValidationErrors((prev) => ({ ...prev, [index]: ruleErrors }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }

    if (!isValid) {
      showErrorToast({
        errors: t('FILL_REQUIRED_FIELDS_ERROR'),
      });
    }

    return isValid;
  };

  const handleSaveRule = async (index: number) => {
    if (validateRule(index)) {
      await onSave();
    }
  };

  const handleDeleteRule = async (index: number) => {
    const curr = form.getValues().guardrail?.custom_rules || [];
    const updated = curr.filter((_, i) => i !== index);
    form.setValue('guardrail.custom_rules', updated);
    await onSave();
  };

  const handleAddRule = () => {
    const curr = form.getValues().guardrail?.custom_rules || [];
    const next = [
      ...curr,
      {
        name: '',
        pattern: '',
        severity: 'medium',
        auto_block: false,
        message: '',
        enable_pre: true,
        enable_post: true,
      },
    ];
    form.setValue('guardrail.custom_rules', next);
  };

  const handleSeverityChange = (index: number, value: string) => {
    if (value === 'block') {
      form.setValue(`guardrail.custom_rules.${index}.severity`, 'critical');
      form.setValue(`guardrail.custom_rules.${index}.auto_block`, true);
    } else {
      form.setValue(`guardrail.custom_rules.${index}.severity`, value);
      form.setValue(`guardrail.custom_rules.${index}.auto_block`, false);
    }
  };

  const getDisplaySeverity = (index: number): string => {
    const severity = form.watch(`guardrail.custom_rules.${index}.severity`);
    const autoBlock = form.watch(`guardrail.custom_rules.${index}.auto_block`);

    if (autoBlock && severity === 'critical') {
      return 'block';
    }
    return severity || 'medium';
  };

  if (!isCustomRulesEnabled) {
    return null;
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="space-y-4">
        {customRules.map((_, index) => {
          const displaySeverity = getDisplaySeverity(index);
          const severityColor = getSeverityColor(displaySeverity);

          return (
            <div key={index} className="space-y-4 rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-high-emphasis">
                  {t('RULE_INDEX', { index: index + 1 })}
                </span>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${severityColor}`}>
                    {displaySeverity === 'block'
                      ? t('BLOCK')
                      : `${displaySeverity.charAt(0).toUpperCase() + displaySeverity.slice(1)} (${(
                          SEVERITY_SCORES[displaySeverity] ?? 0
                        ).toFixed(2)})`}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRule(index)}
                    className="h-8 w-8 p-0 text-medium-emphasis hover:text-error"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className={`grid gap-4 ${isXlBreakpoint ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <FormField
                  control={form.control}
                  name={`guardrail.custom_rules.${index}.name`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">{t('RULE_NAME')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder={t('WRITE_HERE_PLACEHOLDER')}
                          className={
                            validationErrors[index]?.includes(t('RULE_NAME_REQUIRED'))
                              ? 'border-red-500'
                              : ''
                          }
                        />
                      </FormControl>
                      {validationErrors[index]?.includes(t('RULE_NAME_REQUIRED')) && (
                        <p className="mt-1 text-xs text-error">{t('RULE_NAME_REQUIRED')}</p>
                      )}
                    </FormItem>
                  )}
                />

                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('SEVERITY')}</FormLabel>
                  <FormControl>
                    <Select
                      value={getDisplaySeverity(index)}
                      onValueChange={(value) => handleSeverityChange(index, value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          {getDisplaySeverity(index) === 'block'
                            ? t('BLOCK')
                            : `${
                                getDisplaySeverity(index).charAt(0).toUpperCase() +
                                getDisplaySeverity(index).slice(1)
                              } (${(SEVERITY_SCORES[getDisplaySeverity(index)] ?? 0).toFixed(2)})`}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('LOW_SEVERITY_DISPLAY')}</SelectItem>
                        <SelectItem value="medium">{t('MEDIUM_SEVERITY_DISPLAY')}</SelectItem>
                        <SelectItem value="high">{t('HIGH_SEVERITY_DISPLAY')}</SelectItem>
                        <SelectItem value="critical">{t('CRITICAL_SEVERITY_DISPLAY')}</SelectItem>
                        <SelectItem value="block">{t('BLOCK')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              </div>

              <FormField
                control={form.control}
                name={`guardrail.custom_rules.${index}.pattern`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('PATTERN')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder={t('WRITE_HERE_PLACEHOLDER')}
                        className={
                          validationErrors[index]?.includes(t('PATTERN_REQUIRED'))
                            ? 'border-error'
                            : ''
                        }
                      />
                    </FormControl>
                    {validationErrors[index]?.includes(t('PATTERN_REQUIRED')) && (
                      <p className="mt-1 text-xs text-error">{t('PATTERN_REQUIRED')}</p>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`guardrail.custom_rules.${index}.message`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('MESSAGE')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder={t('WRITE_HERE_PLACEHOLDER')}
                        className={
                          validationErrors[index]?.includes(t('MESSAGE_REQUIRED'))
                            ? 'border-red-500'
                            : ''
                        }
                      />
                    </FormControl>
                    {validationErrors[index]?.includes(t('MESSAGE_REQUIRED')) && (
                      <p className="mt-1 text-xs text-error">{t('MESSAGE_REQUIRED')}</p>
                    )}
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <span className="text-sm font-semibold text-high-emphasis">
                  {t('VALIDATION_STAGES')}
                </span>
                <div
                  className={`grid ${isXlBreakpoint ? 'grid-cols-1 gap-6' : 'grid-cols-2 gap-8'}`}
                >
                  <FormField
                    control={form.control}
                    name={`guardrail.custom_rules.${index}.enable_pre`}
                    render={({ field }) => (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FormLabel htmlFor={`enable-pre-${index}`}>
                            {t('USER_QUERY_VALIDATION')}
                          </FormLabel>
                          <InfoTooltip content={t('USER_QUERY_VALIDATION_TOOLTIP')} />
                        </div>
                        <FormControl>
                          <Switch
                            id={`enable-pre-${index}`}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`guardrail.custom_rules.${index}.enable_post`}
                    render={({ field }) => (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FormLabel>{t('AGENT_RESPONSE_VALIDATION')}</FormLabel>
                          <InfoTooltip content={t('AGENT_RESPONSE_VALIDATION_TOOLTIP')} />
                        </div>
                        <FormControl>
                          <Switch
                            id={`enable-post-${index}`}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end border-t pt-3">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => handleSaveRule(index)}
                >
                  {t('SAVE_RULE')}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {customRules.length > 0 ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddRule}
          className="w-fit"
          disabled={customRules.length >= 5}
        >
          {t('ADD_RULE_COUNT', { current: customRules.length, max: 5 })}
        </Button>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={handleAddRule} className="w-fit">
          {t('ADD_RULE_COUNT', { current: 0, max: 5 })}
        </Button>
      )}
    </div>
  );
};
